import { CarvanaCertifiedSpot, HundredDay, SevenDayGuarantee } from '@carvana/ds-icons';
import React from 'react';
import MakeLogo, { hasMakeLogo } from '|/components/make-logo';
import { isNewVehicle } from '|/helpers';
import { deriveManufacturerWarranty } from '|/helpers/manufacturer-warranty';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import styles from '../price-details.module.css';

interface IncludedWithPurchaseSectionProps {
  summary: ReviewSummaryResponse;
}

const IncludedWithPurchaseSection = ({ summary }: IncludedWithPurchaseSectionProps) => {
  const warrantyRows = deriveManufacturerWarranty(summary.vehicle);
  const make = summary.vehicle?.make;
  // The 100-day warranty and Carvana Certified inspection are reconditioning promises on
  // used inventory; neither applies to a new car.
  const isNewCar = isNewVehicle(summary.vehicle);

  return (
    <section className={styles.majorSection} data-testid="pp_price-details_included-with-purchase">
      <div className={styles.majorHeading}>
        <h3 className={styles.majorTitle}>Included with your purchase</h3>
      </div>

      {warrantyRows.length > 0 && (
        <div className={styles.warrantyBlock} data-testid="pp_price-details_factory-warranty">
          {hasMakeLogo(make) && (
            <span className={styles.makeCircle}>
              <MakeLogo make={make} size={18} />
            </span>
          )}
          <p className={styles.warrantyTitle}>Factory Warranty</p>
          <div>
            {warrantyRows.map(row => (
              <p className={styles.warrantyTerms} key={row.key}>
                {row.label}: {row.terms}
              </p>
            ))}
          </div>
          <p className={styles.warrantyNote}>
            See manufacturer&apos;s website for warranty details.
          </p>
        </div>
      )}

      <div className={styles.inclusionGrid}>
        <div className={styles.inclusion} data-testid="pp_price-details_seven-day-guarantee">
          <SevenDayGuarantee size={29} aria-hidden />
          <p className={styles.inclusionLabel}>7-Day Money Back Guarantee</p>
        </div>
        {!isNewCar && (
          <div className={styles.inclusion} data-testid="pp_price-details_limited-warranty">
            <HundredDay size={29} aria-hidden />
            <p className={styles.inclusionLabel}>100-Day Limited Warranty</p>
          </div>
        )}
        {!isNewCar && (
          <div className={styles.inclusion} data-testid="pp_price-details_carvana-certified">
            <CarvanaCertifiedSpot size={29} aria-hidden />
            <p className={styles.inclusionLabel}>Carvana Certified</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default IncludedWithPurchaseSection;
